import { useCallback, useEffect, useRef, useState } from 'react';
import './Home.css';
import { JeffPen, type PenPhase } from '../components/JeffPen/JeffPen.tsx';
import { CameraHud } from '../components/CameraHud/CameraHud.tsx';
import { Countdown } from '../components/Countdown/Countdown.tsx';
import { ResultPanel } from '../components/ResultPanel/ResultPanel.tsx';
import { ReleaseButton } from '../components/ReleaseButton/ReleaseButton.tsx';
import { Logo } from '../components/Logo/Logo.tsx';
import { RecentCalls } from '../components/RecentCalls/RecentCalls.tsx';

import type { JeffCall } from '../../shared/types.ts';
import { JEFF, RESULT_HEADLINES } from '../config/jeff.ts';
import { type JeffPath, planPath } from '../lib/jeffEngine.ts';
import { type Decision, createSeededRng, decide, rngPick, secureSeed } from '../lib/random.ts';
import { ASSETS } from '../../shared/assets.ts';
import { createCall, persistCall } from '../lib/calls.ts';
import { rollEasterEgg } from '../lib/easterEggs.ts';
import { useReducedMotion } from '../lib/hooks.ts';
import type { JeffAudio } from '../lib/audio.ts';

interface HomeProps {
  audio: JeffAudio;
}

/**
 * Brings the pen into view when the countdown starts.
 *
 * On a phone the pen can sit below the fold, and firing a minute-long
 * animation somewhere the user cannot see it is the worst possible outcome.
 * Reads layout once, on a click — never during the walk.
 */
function revealStage(stage: HTMLElement | null, reducedMotion: boolean): void {
  if (!stage) return;

  const box = stage.getBoundingClientRect();
  if (box.top >= 56 && box.bottom <= window.innerHeight) return;

  const target = Math.max(0, window.scrollY + box.top - Math.max(24, (window.innerHeight - box.height) / 2));
  const from = window.scrollY;

  if (reducedMotion) {
    window.scrollTo({ top: target, behavior: 'auto' });
    return;
  }

  window.scrollTo({ top: target, behavior: 'smooth' });

  // Smooth scrolling is silently ignored in some embedded webviews and under
  // some OS settings. Getting the pen on screen matters more than getting
  // there gracefully, so if nothing has moved shortly after, jump.
  setTimeout(() => {
    if (Math.abs(window.scrollY - from) < 1) {
      window.scrollTo({ top: target, behavior: 'auto' });
    }
  }, 250);
}

/**
 * The main event.
 *
 * The ordering here is the whole product promise, so it is worth stating
 * plainly: `decide()` runs the instant RELEASE is pressed, before the countdown
 * begins and before a path exists. `planPath` then receives that direction as
 * an input. There is no branch anywhere downstream that can reach back and
 * change it — not the countdown, not the walk, not an easter egg.
 */
export function Home({ audio }: HomeProps) {
  const reducedMotion = useReducedMotion();

  const [phase, setPhase] = useState<PenPhase>('idle');
  const [run, setRun] = useState(0);

  const [decision, setDecision] = useState<Decision | null>(null);
  const [path, setPath] = useState<JeffPath | null>(null);
  const [call, setCall] = useState<JeffCall | null>(null);
  const [headline, setHeadline] = useState<string>(RESULT_HEADLINES[0]);
  const [egg, setEgg] = useState<string | null>(null);

  // Bumped once a call has been written to localStorage, to remount the history
  // list. Without it "Your recent calls" only ever updates on a page reload.
  const [historyVersion, setHistoryVersion] = useState(0);
  const revealTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const stageRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => () => clearTimeout(revealTimer.current), []);

  const busy = phase === 'countdown' || phase === 'walking';

  const release = useCallback(() => {
    // ── The decision. Cryptographic, and made here — before anything moves. ──
    const drawn = decide(JEFF.longProbability);

    const seed = secureSeed();
    const planned = planPath({
      direction: drawn.direction,
      seed,
      minMs: JEFF.decisionMinMs,
      maxMs: JEFF.decisionMaxMs,
      hardCapMs: JEFF.decisionHardCapMs,
      commitMs: JEFF.commitMs,
      reducedMotion,
      reducedMotionMs: JEFF.reducedMotionMs,
    });

    // Cosmetic randomness is seeded off the same draw so a given call always
    // presents the same way, without touching the CSPRNG again.
    const flavourRng = createSeededRng(seed ^ 0x9e3779b9);

    // Drawn from the cosmetic stream, not the CSPRNG. The market is flavour; the
    // direction is the only thing that gets real entropy, and keeping them apart
    // is what stops a seeded replay from ever reproducing an outcome.
    const asset = JEFF.enableAssets ? rngPick(flavourRng, ASSETS) : null;

    setDecision(drawn);
    setPath(planned);
    setCall(createCall({ direction: drawn.direction, entropy: drawn.entropy, asset }));
    setHeadline(rngPick(flavourRng, RESULT_HEADLINES));
    // Rolled up front, from the same seeded stream, so a given call always tells
    // the same joke. It cannot see the outcome.
    setEgg(rollEasterEgg('result', flavourRng, JEFF.enableEasterEggs)?.text ?? null);
    setRun((n) => n + 1);
    setPhase('countdown');

    if (JEFF.enableAudio) {
      audio.unlock();
      void audio.startAmbience();
      void audio.gate();
    }
  }, [audio, reducedMotion]);

  // Bring the pen into view once the countdown has actually mounted. Doing this
  // from the click handler instead races the browser's own post-click scroll
  // handling and loses.
  useEffect(() => {
    if (phase !== 'countdown') return;
    revealStage(stageRef.current, reducedMotion);
  }, [phase, reducedMotion]);

  const onCountdownComplete = useCallback(() => {
    setPhase('walking');
    if (JEFF.enableAudio) void audio.cluck();
  }, [audio]);

  const onArrived = useCallback(() => {
    revealTimer.current = setTimeout(() => {
      setPhase('result');
      if (JEFF.enableAudio && decision) audio.sting(decision.direction);
    }, JEFF.revealDelayMs);
  }, [audio, decision]);

  // Persist once, when the result lands. Never during the walk — a call that
  // was abandoned mid-animation is not a call.
  useEffect(() => {
    if (phase !== 'result' || !call) return;
    const controller = new AbortController();
    // The return value said where the call ended up, which only the share sheet
    // ever reported. All that matters now is that it landed somewhere.
    void persistCall(call, controller.signal).then(() => {
      setHistoryVersion((n) => n + 1);
    });
    return () => controller.abort();
  }, [phase, call]);

  const newCall = useCallback(() => {
    clearTimeout(revealTimer.current);
    setPhase('idle');
    setPath(null);
    setCall(null);
  }, []);

  return (
    <main className="home" id="main">
      <div className="home__rail">
        <section className="hero">
          <h1 className="hero__logo">
            <Logo size="lg" />
          </h1>
          <p className="hero__tagline">{JEFF.tagline}</p>
        </section>

        <ReleaseButton onRelease={release} disabled={busy} asset={busy ? call?.asset : null} />

        {JEFF.enableHistory && <RecentCalls key={historyVersion} />}
      </div>

      <div className="home__stage" ref={stageRef}>
        <JeffPen
          phase={phase}
          path={path}
          chosen={phase === 'result' ? (decision?.direction ?? null) : null}
          onArrived={onArrived}
        >
          <CameraHud />

          {phase === 'countdown' && (
            <Countdown
              key={run}
              from={JEFF.countdownSeconds}
              onComplete={onCountdownComplete}
              onTick={(n) => JEFF.enableAudio && audio.pip(n === 1)}
            />
          )}

          {phase === 'result' && call && (
            <ResultPanel
              call={call}
              headline={headline}
              egg={egg}
              onNewCall={newCall}
            />
          )}
        </JeffPen>

      </div>

    </main>
  );
}
